import React, { useState } from 'react';
import ImageCropModal from '../../common/ImageCropModal';
import { useBuilderStore } from '../../../store/checkoutBuilder/BuilderContext';
import { createPortal } from 'react-dom';
import type { ColumnNode, RowNode, ComponentNode } from '../../../store/checkoutBuilder/types';
import TimerInspector from './TimerInspector';

const Inspector: React.FC = () => {
    const { state, addColumn, deleteNode, updateComponentProps, addComponent, selectNode, duplicateNode, setRowLayout, setCheckoutLayout } = useBuilderStore();
    const { document, selectionId, ui } = state;
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [confirmRemoveColumnOpen, setConfirmRemoveColumnOpen] = useState(false);
    // Crop state for image component
    const [imgCropOpen, setImgCropOpen] = useState(false);
    const [imgTemp, setImgTemp] = useState<string | null>(null);

    if (!selectionId) {
        return (
            <div className="h-full rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                <div className="text-sm text-gray-600 dark:text-gray-300">Nada selecionado</div>
                <p className="text-xs text-gray-500 mt-1">Selecione uma linha ou coluna para editar.</p>
            </div>
        );
    }

    // Caso especial: configurações da linha do checkout
    if (selectionId === 'checkout-row-config') {
        const currentLayout = (document.meta as any)?.checkoutLayout || 'two-columns';
        
        return (
            <div className="h-full rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium">Configurações do Checkout</h3>
                    <button
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                        title="Fechar"
                        onClick={() => selectNode(null)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Layout da Linha
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            {/* Botão 1 Coluna */}
                            <button
                                onClick={() => {
                                    if (currentLayout === 'two-columns') {
                                        // Mostrar modal de confirmação para remover coluna lateral
                                        setConfirmRemoveColumnOpen(true);
                                    } else {
                                        // Já está em 1 coluna, não faz nada
                                        console.log('Já está em 1 coluna');
                                    }
                                }}
                                className={`p-3 rounded-lg border-2 transition-all text-center ${
                                    currentLayout === 'one-column'
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-300 bg-white hover:border-gray-400 text-gray-700'
                                }`}
                            >
                                <div className="text-xs font-medium mb-2">1 Col (12)</div>
                                <div className="h-6 bg-gray-300 rounded"></div>
                            </button>

                            {/* Botão 2 Colunas */}
                            <button
                                onClick={() => {
                                    if (currentLayout === 'one-column') {
                                        // Mudar para 2 colunas diretamente
                                        setCheckoutLayout('two-columns');
                                    } else {
                                        // Já está em 2 colunas, não faz nada
                                        console.log('Já está em 2 colunas');
                                    }
                                }}
                                className={`p-3 rounded-lg border-2 transition-all text-center ${
                                    currentLayout === 'two-columns'
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-300 bg-white hover:border-gray-400 text-gray-700'
                                }`}
                            >
                                <div className="text-xs font-medium mb-2">2 Col (6/6)</div>
                                <div className="flex gap-1">
                                    <div className="flex-1 h-6 bg-gray-300 rounded"></div>
                                    <div className="flex-1 h-6 bg-gray-300 rounded"></div>
                                </div>
                            </button>
                        </div>
                    </div>
                    
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500">
                            A linha do checkout é fixa e não pode ser excluída. 
                            Use as opções acima para configurar o layout.
                        </p>
                    </div>
                </div>

                {/* Modal de confirmação para remover coluna lateral */}
                {confirmRemoveColumnOpen && createPortal(
                    <div
                        className="fixed inset-0 z-40 flex items-center justify-center"
                        onMouseDown={(e) => { e.stopPropagation(); }}
                    >
                        <div className="absolute inset-0 bg-black/60" onMouseDown={(e) => { e.stopPropagation(); }} />
                        <div className="relative z-50 w-[90%] sm:w-[420px] mx-auto">
                            <div className="bg-white rounded-lg p-6 shadow-xl">
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    Remover Coluna Lateral
                                </h3>
                                <p className="text-sm text-gray-600 mb-6">
                                    A coluna lateral será removida e todos os componentes nela serão perdidos. 
                                    Esta ação não pode ser desfeita.
                                </p>
                                <div className="flex flex-col gap-3">
                                    <button
                                        type="button"
                                        className="w-full py-3 rounded-md bg-red-600 text-white font-medium shadow hover:bg-red-500"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmRemoveColumnOpen(false);
                                            // Mudar para 1 coluna, removendo a coluna lateral
                                            setCheckoutLayout('one-column');
                                        }}
                                    >
                                        Remover Coluna
                                    </button>
                                    <button
                                        type="button"
                                        className="w-full py-3 rounded-md bg-white text-gray-900 font-medium shadow border border-gray-200 hover:bg-gray-50"
                                        onClick={(e) => { e.stopPropagation(); setConfirmRemoveColumnOpen(false); }}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>,
                    window.document.body
                )}
            </div>
        );
    }

    const node = document.nodes[selectionId] as RowNode | ColumnNode | ComponentNode | undefined;
    if (!node) {
        return null;
    }

    if (node.type === 'row') {
        return (
            <div className="h-full rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                {/* Header with action icons */}
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">Linha</h3>
                    <div className="flex items-center gap-2">
                        {/* Delete (opens modal) */}
                        <button
                            className="p-1.5 rounded hover:bg-red-50 text-red-600"
                            title="Excluir"
                            onMouseDown={(e) => { e.preventDefault(); setConfirmDeleteOpen(true); }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 3h6m-9 4h12m-10 0v12m8-12v12M9 3l1 1h4l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                        </button>
                        {/* Duplicate */}
                        <button
                            className="p-1.5 rounded hover:bg-indigo-50 text-indigo-600"
                            title="Duplicar"
                            onMouseDown={(e) => { e.preventDefault(); duplicateNode(node.id); }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="7" y="7" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" /><rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" /></svg>
                        </button>
                        {/* Close (deselect) */}
                        <button
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                            title="Fechar"
                            onMouseDown={(e) => { e.preventDefault(); selectNode(null); }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                        </button>
                    </div>
                </div>
                <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                    Colunas: {node.children.length}
                </div>
                {/* Layout presets */}
                <div className="mt-4">
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2">Layout da linha</label>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { label: '1 Col (12)', layout: [12] },
                            { label: '2 Col (6/6)', layout: [6, 6] },
                            { label: '2 Col (4/8)', layout: [4, 8] },
                            { label: '2 Col (8/4)', layout: [8, 4] },
                            { label: '3 Col (4/4/4)', layout: [4, 4, 4] },
                            { label: '4 Col (3/3/3/3)', layout: [3, 3, 3, 3] },
                        ].map((p) => (
                            <button
                                key={p.label}
                                onClick={() => setRowLayout(node.id, p.layout)}
                                className="border border-gray-200 dark:border-gray-700 rounded-md p-3 hover:bg-gray-50 dark:hover:bg-gray-900 text-left"
                            >
                                <div className="text-[11px] font-medium text-gray-700 dark:text-gray-200 mb-2">{p.label}</div>
                                <div className="flex gap-1">
                                    {p.layout.map((w, i) => (
                                        <div key={i} className="h-2 bg-gray-300 dark:bg-gray-700 rounded" style={{ width: `${(w / 12) * 100}%` }} />
                                    ))}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
                {/* Add column */}
                <div className="mt-4">
                    <button
                        className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => addColumn(node.id)}
                    >
                        + Adicionar coluna
                    </button>
                </div>

                {/* Delete confirmation modal (portal) */}
                {confirmDeleteOpen && createPortal(
                    <div
                        className="fixed inset-0 z-40 flex items-center justify-center"
                        onMouseDown={(e) => { e.stopPropagation(); }}
                    >
                        <div className="absolute inset-0 bg-black/60" onMouseDown={(e) => { e.stopPropagation(); }} />
                        <div className="relative z-50 w-[90%] sm:w-[420px] mx-auto">
                            <div className="flex flex-col items-stretch gap-3">
                                <button
                                    type="button"
                                    className="w-full py-3 rounded-md bg-red-600 text-white font-medium shadow hover:bg-red-500"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmDeleteOpen(false);
                                        deleteNode(node.id);
                                    }}
                                >
                                    Excluir linha
                                </button>
                                <button
                                    type="button"
                                    className="w-full py-3 rounded-md bg-white text-gray-900 font-medium shadow border border-gray-200 hover:bg-gray-50"
                                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(false); }}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>,
                    window.document.body
                )}
            </div>
        );
    }

    if (node.type === 'column') {
        const col = node as ColumnNode;
        const span = col.props.widthByBp[ui.breakpoint] ?? 12;
        return (
            <div className="h-full rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">Coluna</h3>
                    <button
                        className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => deleteNode(col.id)}
                    >
                        Excluir
                    </button>
                </div>
                <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                    Largura ({ui.breakpoint}): {span}/12
                </div>
                <p className="mt-2 text-xs text-gray-500">Edição de largura por breakpoint entra no M2.</p>
            </div>
        );
    }

    if (node.type === 'component') {
        const cmp = node as ComponentNode;
        if (cmp.compType === 'texto') {
            const duplicate = () => {
                const parentId = (cmp.parentId as string) || '';
                const parent = document.nodes[parentId] as ColumnNode | undefined;
                if (!parent || parent.type !== 'column') return;
                const index = parent.children.indexOf(cmp.id);
                addComponent(parentId, cmp.compType, index + 1, { ...(cmp.props || {}) });
            };
            return (
                <div className="h-full rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium">Texto</h3>
                        <div className="flex items-center gap-2">
                            {/* Excluir (abre modal customizado) */}
                            <button
                                className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                title="Excluir"
                                onMouseDown={(e) => { e.preventDefault(); setConfirmDeleteOpen(true); }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 3h6m-9 4h12m-10 0v12m8-12v12M9 3l1 1h4l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                            </button>
                            {/* Duplicar */}
                            <button
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-700"
                                title="Duplicar"
                                onMouseDown={(e) => { e.preventDefault(); duplicate(); }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="7" y="7" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                                    <rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                                </svg>
                            </button>
                            {/* Fechar (desselecionar) */}
                            <button
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-700"
                                title="Fechar"
                                onMouseDown={(e) => { e.preventDefault(); selectNode(null); }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <label className="block text-xs text-gray-600 dark:text-gray-400 mt-3 mb-1">Conteúdo</label>
                    <textarea
                        className="w-full text-sm border border-gray-300 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-950"
                        rows={5}
                        value={(cmp.props?.text as string) ?? ''}
                        onChange={(e) => updateComponentProps(cmp.id, { text: e.target.value })}
                        placeholder="Digite o texto..."
                    />

                    {/* Configurações visuais */}
                    <div className="grid grid-cols-2 gap-3 mt-4">
                        <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Background</label>
                            <input
                                type="color"
                                className="w-full h-8 p-0 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-950"
                                value={typeof cmp.props?.background === 'string' ? (cmp.props.background as string) : '#ffffff'}
                                onChange={(e) => updateComponentProps(cmp.id, { background: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Cor da borda</label>
                            <input
                                type="color"
                                className="w-full h-8 p-0 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-950"
                                value={typeof cmp.props?.borderColor === 'string' ? (cmp.props.borderColor as string) : '#ffffff'}
                                onChange={(e) => updateComponentProps(cmp.id, { borderColor: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                        <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Largura da borda (px)</label>
                            <input
                                type="number"
                                min={0}
                                className="w-full text-sm border border-gray-300 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-950"
                                value={typeof cmp.props?.borderWidth === 'number' ? (cmp.props.borderWidth as number) : 0}
                                onChange={(e) => updateComponentProps(cmp.id, { borderWidth: Math.max(0, parseInt(e.target.value || '0', 10)) })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Radius (px)</label>
                            <input
                                type="number"
                                min={0}
                                className="w-full text-sm border border-gray-300 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-950"
                                value={typeof cmp.props?.radius === 'number' ? (cmp.props.radius as number) : 8}
                                onChange={(e) => updateComponentProps(cmp.id, { radius: Math.max(0, parseInt(e.target.value || '0', 10)) })}
                            />
                        </div>
                    </div>

                    {/* Modal de confirmação de exclusão (portal) */}
                    {confirmDeleteOpen && createPortal(
                        <div
                            className="fixed inset-0 z-40 flex items-center justify-center"
                            onMouseDown={(e) => { e.stopPropagation(); }}
                        >
                            <div className="absolute inset-0 bg-black/60" onMouseDown={(e) => { e.stopPropagation(); }} />
                            <div className="relative z-50 w-[90%] sm:w-[420px] mx-auto">
                                <div className="flex flex-col items-stretch gap-3">
                                    <button
                                        type="button"
                                        className="w-full py-3 rounded-md bg-red-600 text-white font-medium shadow hover:bg-red-500"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmDeleteOpen(false);
                                            deleteNode(cmp.id);
                                        }}
                                    >
                                        Excluir componente
                                    </button>
                                    <button
                                        type="button"
                                        className="w-full py-3 rounded-md bg-white text-gray-900 font-medium shadow border border-gray-200 hover:bg-gray-50"
                                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(false); }}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>,
                        window.document.body
                    )}
                </div>
            );
        }
        if (cmp.compType === 'imagem') {
            const duplicate = () => {
                const parentId = (cmp.parentId as string) || '';
                const parent = document.nodes[parentId] as ColumnNode | undefined;
                if (!parent || parent.type !== 'column') return;
                const index = parent.children.indexOf(cmp.id);
                addComponent(parentId, cmp.compType, index + 1, { ...(cmp.props || {}) });
            };
            const onPickFile = (file: File) => {
                const accepted = ['image/png', 'image/jpeg'];
                if (!accepted.includes(file.type)) return;
                if (file.size > 5 * 1024 * 1024) return; // 5MB
                const reader = new FileReader();
                reader.onload = (e) => {
                    setImgTemp(e.target?.result as string);
                    setImgCropOpen(true);
                };
                reader.readAsDataURL(file);
            };
            const inputId = `img-upload-${cmp.id}`;
            const currentSrc = (cmp.props?.imageUrl as string) || (cmp.props?.src as string) || '';
            return (
                <div className="h-full rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                    {/* Header with icons */}
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium">Imagem</h3>
                        <div className="flex items-center gap-2">
                            {/* Delete with modal */}
                            <button
                                className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                title="Excluir"
                                onMouseDown={(e) => { e.preventDefault(); setConfirmDeleteOpen(true); }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 3h6m-9 4h12m-10 0v12m8-12v12M9 3l1 1h4l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                            </button>
                            {/* Duplicate */}
                            <button
                                className="p-1.5 rounded hover:bg-indigo-50 text-indigo-600"
                                title="Duplicar"
                                onMouseDown={(e) => { e.preventDefault(); duplicate(); }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="7" y="7" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" /><rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" /></svg>
                            </button>
                            {/* Close (deselect) */}
                            <button
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                                title="Fechar"
                                onMouseDown={(e) => { e.preventDefault(); selectNode(null); }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                            </button>
                        </div>
                    </div>

                    {/* Preview (se existir imagem) */}
                    {currentSrc && (
                        <div className="relative mt-3 rounded-md overflow-hidden border border-gray-200 bg-white dark:bg-gray-950" onMouseDown={(e) => e.stopPropagation()}>
                            <img
                                src={currentSrc}
                                alt={(cmp.props?.alt as string) || 'Imagem'}
                                className="block"
                                style={{ width: 'auto', height: 'auto', maxWidth: '100%' }}
                            />
                            <div className="absolute right-2 top-2 z-10 flex gap-1">
                                {/* Recrop */}
                                <button
                                    className="p-1.5 rounded bg-white/90 hover:bg-white text-gray-700 shadow"
                                    title="Cortar novamente"
                                    onMouseDown={(e) => { e.preventDefault(); setImgTemp(currentSrc); setImgCropOpen(true); }}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7 3v14a2 2 0 0 0 2 2h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M3 7h14a2 2 0 0 1 2 2v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </button>
                                {/* Remove image */}
                                <button
                                    className="p-1.5 rounded bg-white/90 hover:bg-white text-red-600 shadow"
                                    title="Remover imagem"
                                    onMouseDown={(e) => { e.preventDefault(); updateComponentProps(cmp.id, { src: undefined, imageUrl: undefined }); }}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 3h6m-9 4h12m-10 0v12m8-12v12M9 3l1 1h4l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Upload box (oculto quando há imagem) */}
                    {!currentSrc && (
                        <div className="mt-3">
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Imagem</label>
                            <label htmlFor={inputId} className="block border border-dashed border-gray-300 rounded-md p-5 text-center cursor-pointer hover:bg-gray-50">
                                <div className="flex flex-col items-center">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="14" rx="2" stroke="#94a3b8" strokeWidth="2" /><path d="M7 13l3-4 3 3 3-5 3 6" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="9" cy="8" r="1.2" fill="#cbd5e1" /></svg>
                                    <span className="text-indigo-600 font-medium">Selecione do computador</span>
                                    <span className="text-xs text-gray-500">ou arraste aqui<br />PNG, JPG até 5 MB</span>
                                </div>
                            </label>
                            <input
                                id={inputId}
                                type="file"
                                accept="image/png,image/jpeg"
                                className="hidden"
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickFile(f); }}
                            />
                        </div>
                    )}

                    {/* Crop Modal (portal) */}
                    {imgCropOpen && imgTemp && createPortal(
                        <ImageCropModal
                            imageSrc={imgTemp}
                            open={imgCropOpen}
                            onClose={() => { setImgCropOpen(false); setImgTemp(null); }}
                            onCropComplete={(cropped: string) => {
                                updateComponentProps(cmp.id, { src: cropped, imageUrl: cropped });
                                setImgCropOpen(false);
                                setImgTemp(null);
                            }}
                        />,
                        window.document.body
                    )}

                    {/* Alignment */}
                    <div className="mt-4">
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Alinhamento</label>
                        <div className="grid grid-cols-3 gap-2">
                            <button onClick={() => updateComponentProps(cmp.id, { align: 'left' })} className={`px-3 py-2 rounded border ${cmp.props?.align === 'left' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="4" y="6" width="16" height="2" rx="1" fill="#555" /><rect x="4" y="11" width="10" height="2" rx="1" fill="#bbb" /><rect x="4" y="16" width="7" height="2" rx="1" fill="#bbb" /></svg>
                            </button>
                            <button onClick={() => updateComponentProps(cmp.id, { align: 'center' })} className={`px-3 py-2 rounded border ${(!cmp.props?.align || cmp.props?.align === 'center') ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="4" y="6" width="16" height="2" rx="1" fill="#555" /><rect x="7" y="11" width="10" height="2" rx="1" fill="#bbb" /><rect x="9" y="16" width="6" height="2" rx="1" fill="#bbb" /></svg>
                            </button>
                            <button onClick={() => updateComponentProps(cmp.id, { align: 'right' })} className={`px-3 py-2 rounded border ${cmp.props?.align === 'right' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="4" y="6" width="16" height="2" rx="1" fill="#555" /><rect x="10" y="11" width="10" height="2" rx="1" fill="#bbb" /><rect x="13" y="16" width="7" height="2" rx="1" fill="#bbb" /></svg>
                            </button>
                        </div>
                    </div>

                    {/* Redirect URL */}
                    <div className="mt-4">
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">URL de redirecionamento</label>
                        <input
                            className="w-full text-sm border border-gray-300 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-950"
                            type="url"
                            value={(cmp.props?.href as string) ?? ''}
                            onChange={(e) => updateComponentProps(cmp.id, { href: e.target.value })}
                            placeholder="https://..."
                        />
                    </div>

                    {/* Modal de confirmação de exclusão (portal) */}
                    {confirmDeleteOpen && createPortal(
                        <div
                            className="fixed inset-0 z-40 flex items-center justify-center"
                            onMouseDown={(e) => { e.stopPropagation(); }}
                        >
                            <div className="absolute inset-0 bg-black/60" onMouseDown={(e) => { e.stopPropagation(); }} />
                            <div className="relative z-50 w-[90%] sm:w-[420px] mx-auto">
                                <div className="flex flex-col items-stretch gap-3">
                                    <button
                                        type="button"
                                        className="w-full py-3 rounded-md bg-red-600 text-white font-medium shadow hover:bg-red-500"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmDeleteOpen(false);
                                            deleteNode(cmp.id);
                                        }}
                                    >
                                        Excluir componente
                                    </button>
                                    <button
                                        type="button"
                                        className="w-full py-3 rounded-md bg-white text-gray-900 font-medium shadow border border-gray-200 hover:bg-gray-50"
                                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(false); }}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>,
                        window.document.body
                    )}
                </div>
            );
        }
        if (cmp.compType === 'cronometro') {
            const duplicate = () => {
                const parentId = (cmp.parentId as string) || '';
                const parent = document.nodes[parentId] as ColumnNode | undefined;
                if (!parent || parent.type !== 'column') return;
                const index = parent.children.indexOf(cmp.id);
                addComponent(parentId, cmp.compType, index + 1, { ...(cmp.props || {}) });
            };
            
            return (
                <div className="h-full rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-medium">Cronômetro</h3>
                        <div className="flex items-center gap-2">
                            <button
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                                title="Duplicar"
                                onClick={duplicate}
                            >
                                <i className="ri-file-copy-line text-sm"></i>
                            </button>
                            <button
                                className="p-1.5 rounded hover:bg-red-100 text-red-500"
                                title="Excluir"
                                onClick={() => setConfirmDeleteOpen(true)}
                            >
                                <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                            <button
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                                title="Fechar"
                                onClick={() => selectNode(null)}
                            >
                                <i className="ri-close-line text-sm"></i>
                            </button>
                        </div>
                    </div>

                    <TimerInspector
                        config={cmp.props || {}}
                        onConfigChange={(newConfig) => {
                            updateComponentProps(cmp.id, { ...(cmp.props || {}), ...newConfig });
                        }}
                    />

                    {/* Modal de confirmação de exclusão */}
                    {confirmDeleteOpen && createPortal(
                        <div
                            className="fixed inset-0 z-40 flex items-center justify-center"
                            onMouseDown={(e) => { e.stopPropagation(); }}
                        >
                            <div className="absolute inset-0 bg-black/60" onMouseDown={(e) => { e.stopPropagation(); }} />
                            <div className="relative z-50 w-[90%] sm:w-[420px] mx-auto">
                                <div className="flex flex-col items-stretch gap-3">
                                    <button
                                        type="button"
                                        className="w-full py-3 rounded-md bg-red-600 text-white font-medium shadow hover:bg-red-500"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmDeleteOpen(false);
                                            deleteNode(cmp.id);
                                        }}
                                    >
                                        Excluir cronômetro
                                    </button>
                                    <button
                                        type="button"
                                        className="w-full py-3 rounded-md bg-white text-gray-900 font-medium shadow border border-gray-200 hover:bg-gray-50"
                                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(false); }}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>,
                        window.document.body
                    )}
                </div>
            );
        }
        return (
            <div className="h-full rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                <div className="text-xs text-gray-500">Tipo de componente não suportado para edição.</div>
            </div>
        );
    }

    return null;
};

export default Inspector;
